import { Text, Card, Group, Button, Space, Anchor } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { IconCalendar, IconFlame } from "@tabler/icons-react";
import { MouseEvent, useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { TCronogramaForm } from "@/modules/desafio-cronograma/firestore";
import cronogramaSchema from "@/modules/desafio-cronograma/schema";
import useUserStore from "@/modules/users/store";
import { useThemeTokens } from "@/theme/useThemeTokens";
import useGetAllDesafios from "./useGetAllDesafios";
import getCronograma from "./useGetCronograma";
import useGetDesafioByElementId from "./useGetDesafioById";

const message = "Ocorreu um erro ao carregar os dados:";
const MAX_TITLE_SIZE = 120;
const CARD_HEIGHT = 380;

const Background = ({ children, color, isMobile }: { children: React.ReactNode; color: string; isMobile: boolean }) => {
  return (
    <Card
      withBorder
      p="md"
      bg={color}
      radius="md"
      w={isMobile ? "100%" : 250}
      miw={isMobile ? "100%" : 250}
      h={isMobile ? "auto" : CARD_HEIGHT}
      mih={CARD_HEIGHT}
      style={{ display: "flex", flexDirection: "column" }}
    >
      {children}
    </Card>
  );
};

const CronogramasDeDesafiosCard = () => {
  const { colors } = useThemeTokens();
  const color = colors.primary1;
  const isMobile = useMediaQuery("(max-width: 48em)") ?? false;

  const [data, setData] = useState<TCronogramaDesafio[]>([]);
  const [reload, setReload] = useState(false);

  const { activeEmpresaId, colaborador } = useUserStore();

  const { data: desafiosList } = useGetAllDesafios();

  const newData = data.filter((item) => {
    const desafio = desafiosList || [];
    return desafio?.filter((row) => row.id === item.idDesafio)?.length > 0;
  });

  const uniqueData = newData && newData[0];

  const { data: desafio } = useGetDesafioByElementId({ id: uniqueData?.idDesafio || "" });

  useEffect(() => {
    const loadData = async () => {
      const arrayDesafio = await getCronograma(activeEmpresaId || "");
      setData(arrayDesafio);
    };
    const fetchData = async () => {
      try {
        await loadData();
      } catch (error) {
        console.error(message, error);
      }
    };
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchData();
  }, [activeEmpresaId, reload]);

  /**
   * Função que trata o clique do botão.
   * @param event - O evento de clique do botão.
   */
  function handleClick(event: MouseEvent<HTMLButtonElement>) {
    const schema = cronogramaSchema;
    const defaultValues: TCronogramaForm = schema.getDefault() as TCronogramaForm;
    event.preventDefault();
    modals.openContextModal({
      size: "xl",
      innerProps: {
        item: schema.cast(defaultValues, { stripUnknown: true, assert: false }) as TCronogramaForm,
        data: data,
        refetch: () => setReload((reload) => !reload)
      },
      modal: "cronograma-desafios"
    });
  }

  const hasChallenge = !!newData?.length;
  const hasMultipleChallenges = hasChallenge && newData.length > 1;
  const canCreateChallenge = colaborador?.role !== "COLLABORATOR";

  if (hasMultipleChallenges) {
    return (
      <Background color={color} isMobile={isMobile}>
        <Text c={colors.textStrong} fw={700} size="md">
          Visualize os desafios da empresa:
        </Text>
        <Space h="md" />
        <Group justify={"center"} mt="auto" pb="sm">
          <Button bg={color} onClick={handleClick}>
            Visualizar
          </Button>
        </Group>
      </Background>
    );
  }

  if (hasChallenge) {
    return (
      <Background color={color} isMobile={isMobile}>
        <Text c={colors.textStrong} fw={700} size="md">
          Se inscreva para o dia do desafio
        </Text>
        <Space h="md" />
        <Group justify={"flex-start"} align={"center"} style={{ gap: "unset", marginRight: 5 }}>
          <IconFlame color={colors.textStrong} style={{ fontSize: "12" }} />
          <Text c={colors.textStrong} fw={500} size="xs">
            Desafio de inovação
          </Text>
        </Group>
        <Text c={colors.textStrong} fw={600} size="sm">
          {desafio?.titulo && desafio.titulo?.length > MAX_TITLE_SIZE
            ? `${desafio.titulo.slice(0, MAX_TITLE_SIZE)}...`
            : desafio?.titulo}
        </Text>
        <Space h="md" />
        <Group justify={"flex-start"} align={"center"} style={{ gap: "unset" }}>
          <IconCalendar color={colors.textStrong} style={{ fontSize: "12", marginRight: 5 }} />
          <Text c={colors.textStrong} fw={500} size="xs">
            Data de inscrição
          </Text>
        </Group>
        <Text c={colors.textStrong} fw={600} size="sm">
          {uniqueData?.inicioIncricao?.toLocaleDateString("pt-BR") ?? ""} -{" "}
          {uniqueData?.terminoIncricao?.toLocaleDateString("pt-BR") ?? ""}{" "}
        </Text>
        <Space h="md" />
        <Group justify={"center"} mt="auto" pb="sm">
          <Button bg={color} onClick={handleClick}>
            Se inscreva
          </Button>
        </Group>
      </Background>
    );
  }

  if (canCreateChallenge) {
    return (
      <Background color={color} isMobile={isMobile}>
        <Text c={colors.textStrong} fw={700} size="md">
          Agende uma imersão no desafio
        </Text>
        <Space h="xs" />
        <Text c={colors.textStrong} fw={400} size="xs">
          Acesse um desafio existente e cadastre o agendamento da imersão na página de detalhes.
        </Text>
        <Space h="md" />
        <Group justify={"center"} mt="auto" pb="sm">
          <Anchor component={NavLink} c={color} to={`/desafios`} underline="never">
            <Button bg={colors.primary}>Ir para desafios</Button>
          </Anchor>
        </Group>
      </Background>
    );
  }

  return <></>;
};

export default CronogramasDeDesafiosCard;
